import { Autocomplete, Box, Button, CircularProgress, TextField, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "const/axios";
import { finishAuthenticatedLogin } from "utils/postLoginNavigation";
import { getStoredMemberships } from "utils/organizationAccess";
import useKothar from "context/useKothar";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const OrganizationSwitcher = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [, { setToken }] = useKothar();
  const [selectedOrgId, setSelectedOrgId] = useState(null);

  const cachedMemberships = useMemo(() => getStoredMemberships(), []);

  const {
    data: memberships = cachedMemberships,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["auth-memberships"],
    queryFn: async () => {
      const res = await axios.get("auth/memberships");
      return res?.data?.data || [];
    },
    initialData: cachedMemberships,
    placeholderData: cachedMemberships,
    retry: false,
  });

  const options = useMemo(
    () =>
      memberships.map((m) => ({
        id: m.orgId,
        label: m.orgName,
        membershipId: m.membershipId,
        role: m.role,
        organizationDeleted: m.organizationDeleted,
      })),
    [memberships]
  );

  const activeOrgId = useMemo(() => {
    try {
      const detail = JSON.parse(localStorage.getItem("userDetail"));
      return detail?.activeOrgId || detail?.orgId || null;
    } catch {
      return null;
    }
  }, []);

  const { mutate: switchOrg, isLoading: isSwitching } = useMutation({
    mutationFn: async (orgId) => {
      const res = await axios.post("auth/switch-org", { orgId });
      return res.data;
    },
    onSuccess: (responseData) => {
      finishAuthenticatedLogin(responseData, {
        navigate,
        setToken,
        queryClient,
        successMessage: "Organization switched",
      });
    },
    onError: (err) => {
      toast.error(err?.response?.data?.errorMessage || "Could not switch organization");
    },
  });

  if (isLoading) {
    return <CircularProgress size={24} />;
  }

  if (options.length <= 1) {
    const only = options[0];
    return (
      <Box sx={{ maxWidth: 480 }}>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Active organization
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {only
            ? `You belong to one consultancy: ${only.label}. The switcher appears when you have memberships in two or more organizations.`
            : "You have no organization memberships. Platform administrators without memberships manage all organizations from Super Admin → Organisation."}
        </Typography>
        {only && (
          <Typography variant="body1" fontWeight={600}>
            {only.label}
          </Typography>
        )}
      </Box>
    );
  }

  const current =
    options.find((o) => o.id === activeOrgId) ||
    options.find((o) => o.id === selectedOrgId) ||
    null;

  return (
    <Box sx={{ maxWidth: 480 }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Active organization
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Switch between consultancies you belong to. Your permissions and data scope update
        immediately.
      </Typography>
      {isError && memberships.length > 0 && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
          Could not refresh the organization list from the server. Showing your last known
          memberships — you can still switch organization below.
        </Typography>
      )}
      <Autocomplete
        options={options}
        value={current}
        getOptionLabel={(option) => option.label || ""}
        getOptionDisabled={(option) => Boolean(option.organizationDeleted)}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        onChange={(_, value) => setSelectedOrgId(value?.id ?? null)}
        renderInput={(params) => <TextField {...params} label="Organization" size="small" />}
      />
      <Button
        variant="contained"
        sx={{ mt: 2 }}
        disabled={!selectedOrgId || selectedOrgId === activeOrgId || isSwitching}
        onClick={() => switchOrg(selectedOrgId)}
        startIcon={isSwitching ? <CircularProgress size={16} color="inherit" /> : null}
      >
        Switch organization
      </Button>
    </Box>
  );
};

export default OrganizationSwitcher;
